import LexicalParser from 'app/services/formula/parser/lexicalParser';
import SyntaxParser, { syntaxParser } from 'app/services/formula/parser/syntaxParser';
import { deriveIteratorName } from 'app/services/formula/suggestionEngine';
import _ from 'lodash';

import {
  ARRAY_FUNCTIONS_WITH_ITERATORS,
  LEX_TOKEN_TYPES,
  STRING_TYPES,
  SWITCH_FUNCTION_ENUM,
} from 'constants/parser';
import { getUUID } from 'utils/uuid';

import {
  COLLECTION_FUNCTIONS,
  COMPARISON_OPERATORS,
  DATE_FN_SET,
  GROUP_OPERATORS,
  NODE_KIND,
  SET_MEMBERSHIP_MODE,
  UNQUOTED_TYPES,
  VALUE_KIND,
} from './visualBuilderModel.constants';
import {
  astToText,
  emitSpecFieldText,
  isSpecField,
  needsQuotes,
} from './visualBuilderModelAstText';
import { resolveFieldTypeStr } from './visualBuilderModelFieldOptions';

// Arithmetic operator set — mirrors ARITHMETICAL_OPERATORS in constants/parser (not exported)
const ARITHMETIC_OPS = new Set(['+', '-', '*', '/']);

// Array functions usable as values. ANY/ALL excluded (bool return → COLLECTION path).
const AGGREGATE_FNS = new Set([
  'WHERE',
  'MIN_BY',
  'MAX_BY',
  'SUM',
  'SELECT',
  'COUNT',
  'STRING_JOIN',
]);
const PREDICATE_AGGREGATE_FNS = new Set(['WHERE', 'COUNT']);
const SELECTOR_AGGREGATE_FNS = new Set(['MIN_BY', 'MAX_BY', 'SUM', 'SELECT']);

// Functions that are handled as first-class node kinds in the model and must
// NOT be mapped to NODE_KIND.FUNCTION / VALUE_KIND.FUNCTION.
// NOT wrapping a non-negated AND/OR group is modelled as a negated GROUP node;
// all other NOT shapes stay RAW (handled in the dedicated NOT branch below).
// DATE / DATE_NOW are handled by the relativeDate / date-literal comparison paths.
const RESERVED_FN_NAMES = new Set([
  'AND',
  'OR',
  'NOT',
  'SWITCH',
  'CASE',
  'ANY',
  'ALL',
  'DATE_NOW',
  'DATE',
]);

// Operator priority for arithmetic operators — used to decide parenthesisation.
// Higher number = tighter binding. Mirrors OPERATORS_PRIORITY_AST from parser.js.
const ARITH_PRIORITY_MUL_DIV = 30;
const ARITH_PRIORITY_ADD_SUB = 20;

const ARITH_PRIORITY = op => {
  if (op === '*' || op === '/') {
    return ARITH_PRIORITY_MUL_DIV;
  }

  if (op === '+' || op === '-') {
    return ARITH_PRIORITY_ADD_SUB;
  }

  return 0;
};

// A set-membership collapse requires at least this many sibling comparisons;
// a single `path != 'x'` reads more clearly as a plain comparison.
const MIN_SET_MEMBERSHIP_VALUES = 2;

// Internal node constructors

const makeId = () => getUUID();

/**
 * @param {'AND'|'OR'} op
 * @param {Array} [children]
 * @returns {Object}
 */
const makeGroup = (op, children = []) => ({ kind: NODE_KIND.GROUP, op, children, id: makeId() });

/**
 * @returns {Object}
 */
const makeBlankComparison = () => ({
  kind: NODE_KIND.COMPARISON,
  path: '',
  operator: '==',
  value: '',
  valueType: null,
  id: makeId(),
});

/**
 * @param {string} text
 * @returns {Object}
 */
const makeRaw = text => ({ kind: NODE_KIND.RAW, text, id: makeId() });

/**
 * @param {{ condition: Object, result: string, resultType: string|null }} params
 * @returns {Object}
 */
const makeSwitchCase = ({ condition, result, resultType }) => ({
  id: makeId(),
  condition,
  result,
  resultType: resultType ?? null,
});

// Value model constructors (internal)

/**
 * @param {string} path
 * @returns {Object}
 */
const makeFieldRef = path => ({ kind: VALUE_KIND.FIELD_REF, path });

/**
 * @param {string} value
 * @param {string|null} valueType
 * @returns {Object}
 */
const makeLiteral = (value, valueType) => ({ kind: VALUE_KIND.LITERAL, value, valueType });

/**
 * @param {string} fn
 * @param {Array} args
 * @returns {Object}
 */
const makeFunctionValue = (fn, args) => ({ kind: VALUE_KIND.FUNCTION, fn, args, id: makeId() });

/**
 * @param {string} op
 * @param {Object} left
 * @param {Object} right
 * @returns {Object}
 */
const makeArithmeticValue = (op, left, right) => ({
  kind: VALUE_KIND.ARITHMETIC,
  op,
  operands: [left, right],
  id: makeId(),
});

/**
 * @param {string} text
 * @returns {Object}
 */
const makeRawValue = text => ({ kind: VALUE_KIND.RAW, text });

const makeAggregateValue = ({ fn, arraySource, iteratorName, arg, argKind }) => ({
  kind: VALUE_KIND.AGGREGATE,
  fn,
  arraySource,
  iteratorName,
  arg,
  argKind,
  id: makeId(),
});

const makeMemberAccessValue = (target, property) => ({
  kind: VALUE_KIND.MEMBER_ACCESS,
  target,
  property,
});

/**
 * @returns {Object}
 */
const makeBlankSwitchCase = () =>
  makeSwitchCase({ condition: makeGroup(GROUP_OPERATORS.AND), result: '', resultType: null });

// Value model: AST node → valueModel

/**
 * Map an AST node to a valueModel.
 * Returns a raw valueModel for any shape that cannot be losslessly represented.
 *
 * @param {Object|null} astNode
 * @returns {Object}
 */
const astNodeToValueModel = astNode => {
  if (_.isNil(astNode)) {
    return makeRawValue('');
  }

  const { type, value, typeOfValue, operands = [] } = astNode;

  // ---- MEMBER_ACCESS: spec_field (e.g. MIN_BY(...).CreatedDate) --------------
  // operands[0] is the inner function call; value's last segment is the property.
  if (isSpecField(astNode)) {
    const targetVm = astNodeToValueModel(operands[0]);

    if (targetVm.kind !== VALUE_KIND.RAW) {
      return makeMemberAccessValue(targetVm, _.last(String(value ?? '').split('.')));
    }

    return makeRawValue(emitSpecFieldText(astNode));
  }

  if (type === LEX_TOKEN_TYPES.field) {
    return makeFieldRef(value);
  }

  if (
    type === LEX_TOKEN_TYPES.value ||
    type === LEX_TOKEN_TYPES.bool ||
    type === LEX_TOKEN_TYPES.decimal ||
    type === LEX_TOKEN_TYPES.date
  ) {
    if (typeOfValue === 'iterator') {
      // Iterator tokens are synthetic; treat as FIELD_REF so selector paths like
      // _borrowertask1.CreatedDate remain modellable inside AGGREGATE.
      return makeFieldRef(value ?? '');
    }

    return makeLiteral(String(value ?? ''), typeOfValue ?? null);
  }

  if (type === LEX_TOKEN_TYPES.operator && ARITHMETIC_OPS.has(value)) {
    const leftValue = astNodeToValueModel(operands[0]);
    const rightValue = astNodeToValueModel(operands[1]);

    if (leftValue.kind === VALUE_KIND.RAW || rightValue.kind === VALUE_KIND.RAW) {
      return makeRawValue(astToText(astNode));
    }

    // Safety check: would emitting this node require parens that the grammar
    // cannot parse?  The grammar has no parenthesised arithmetic grouping —
    // only function calls use parens.  If an operand would need to be wrapped
    // in parens to preserve evaluation order, fall back to RAW so we never
    // emit un-parseable text.
    const parentPri = ARITH_PRIORITY(value);
    const isNonCommutative = value === '-' || value === '/';

    const rightNeedsParens =
      rightValue.kind === VALUE_KIND.ARITHMETIC &&
      isNonCommutative &&
      ARITH_PRIORITY(rightValue.op) === parentPri;

    const leftNeedsParens =
      leftValue.kind === VALUE_KIND.ARITHMETIC && ARITH_PRIORITY(leftValue.op) < parentPri;

    if (leftNeedsParens || rightNeedsParens) {
      return makeRawValue(astToText(astNode));
    }

    return makeArithmeticValue(value, leftValue, rightValue);
  }

  // ---- AGGREGATE: array functions as values — must precede generic FUNCTION branch.
  if (
    type === LEX_TOKEN_TYPES.function &&
    AGGREGATE_FNS.has(value) &&
    !RESERVED_FN_NAMES.has(value)
  ) {
    const arraySourceVm = operands[0] ? astNodeToValueModel(operands[0]) : makeRawValue('');

    if (arraySourceVm.kind === VALUE_KIND.RAW) {
      return makeRawValue(astToText(astNode));
    }

    // Recover the injected iterator token (operands[1] for WITH_ITERATOR functions).
    const iteratorToken = operands.find(op => op?.typeOfValue === 'iterator');
    const iteratorName = iteratorToken?.value ?? '';

    // The predicate/selector is the last non-iterator, non-arraySource operand.
    const nonIteratorOperands = operands.filter(op => op?.typeOfValue !== 'iterator');
    const argOperand = nonIteratorOperands[1] ?? null;

    // STRING_JOIN or COUNT(array) bare — no predicate/selector.
    if (value === 'STRING_JOIN' || (value === 'COUNT' && !argOperand)) {
      return makeAggregateValue({
        fn: value,
        arraySource: arraySourceVm,
        iteratorName: value === 'STRING_JOIN' ? '' : iteratorName,
        arg: null,
        argKind: 'none',
      });
    }

    if (PREDICATE_AGGREGATE_FNS.has(value) && argOperand) {
      return makeAggregateValue({
        fn: value,
        arraySource: arraySourceVm,
        iteratorName,
        arg: safeAstNodeToModel(argOperand),
        argKind: 'predicate',
      });
    }

    if (SELECTOR_AGGREGATE_FNS.has(value) && argOperand) {
      const argVm = astNodeToValueModel(argOperand);

      if (argVm.kind === VALUE_KIND.RAW) {
        return makeRawValue(astToText(astNode));
      }

      return makeAggregateValue({
        fn: value,
        arraySource: arraySourceVm,
        iteratorName,
        arg: argVm,
        argKind: 'selector',
      });
    }

    return makeRawValue(astToText(astNode));
  }

  if (type === LEX_TOKEN_TYPES.function && !RESERVED_FN_NAMES.has(value)) {
    const isArrayFn = ARRAY_FUNCTIONS_WITH_ITERATORS.includes(value);
    const filteredOperands = isArrayFn
      ? operands.filter(op => op?.typeOfValue !== 'iterator')
      : operands;
    const args = filteredOperands.map(astNodeToValueModel);

    if (args.some(a => a.kind === VALUE_KIND.RAW)) {
      return makeRawValue(astToText(astNode));
    }

    return makeFunctionValue(value, args);
  }

  return makeRawValue(astToText(astNode));
};

/**
 * Return true when a valueModel contains no RAW descendant.
 *
 * @param {Object} vm
 * @returns {boolean}
 */
const valueModelIsClean = vm => {
  if (_.isNil(vm)) {
    return true;
  }

  if (vm.kind === VALUE_KIND.RAW) {
    return false;
  }

  if (vm.kind === VALUE_KIND.FUNCTION) {
    return (vm.args ?? []).every(valueModelIsClean);
  }

  if (vm.kind === VALUE_KIND.ARITHMETIC) {
    return (vm.operands ?? []).every(valueModelIsClean);
  }

  if (vm.kind === VALUE_KIND.AGGREGATE) {
    if (!valueModelIsClean(vm.arraySource)) {
      return false;
    }

    if (vm.argKind === 'predicate') {
      return !containsRaw(vm.arg);
    }

    if (vm.argKind === 'selector') {
      return valueModelIsClean(vm.arg);
    }

    return true;
  }

  if (vm.kind === VALUE_KIND.MEMBER_ACCESS) {
    return valueModelIsClean(vm.target);
  }

  return true;
};

// Value model → text

/**
 * Emit a valueModel back to expression text.
 *
 * @param {Object} vm
 * @returns {string}
 */
const emitValueModel = vm => {
  if (_.isNil(vm)) {
    return '';
  }

  if (vm.kind === VALUE_KIND.RAW) {
    return vm.text ?? '';
  }

  if (vm.kind === VALUE_KIND.FIELD_REF) {
    return vm.path ?? '';
  }

  if (vm.kind === VALUE_KIND.LITERAL) {
    const { value, valueType } = vm;

    if (valueType === 'date') {
      return `DATE('${value}', 'UTC')`;
    }

    return needsQuotes(valueType) ? `'${value}'` : String(value ?? '');
  }

  if (vm.kind === VALUE_KIND.FUNCTION) {
    const argTexts = (vm.args ?? []).map(emitValueModel);

    return `${vm.fn}(${argTexts.join(', ')})`;
  }

  if (vm.kind === VALUE_KIND.ARITHMETIC) {
    const parts = (vm.operands ?? []).map(emitValueModel).filter(Boolean);

    return parts.join(` ${vm.op} `);
  }

  if (vm.kind === VALUE_KIND.AGGREGATE) {
    const { fn, arraySource, arg, argKind } = vm;
    const arrayText = emitValueModel(arraySource);

    if (argKind === 'predicate') {
      return `${fn}(${arrayText}, ${emitModelToText(arg)})`;
    }

    if (argKind === 'selector') {
      return `${fn}(${arrayText}, ${emitValueModel(arg)})`;
    }

    return `${fn}(${arrayText})`;
  }

  if (vm.kind === VALUE_KIND.MEMBER_ACCESS) {
    return `${emitValueModel(vm.target)}.${vm.property}`;
  }

  return '';
};

// AST → model

/**
 * Map a group operator + a comparison operator to a set-membership mode.
 * Only the two lossless idioms collapse:
 *   AND + '!='  →  noneOf
 *   OR  + '=='  →  anyOf
 * Anything else returns null (the group stays a group).
 *
 * @param {'AND'|'OR'} groupOp
 * @param {string} comparisonOp
 * @returns {string|null}
 */
const resolveSetMembershipMode = (groupOp, comparisonOp) => {
  if (groupOp === GROUP_OPERATORS.AND && comparisonOp === '!=') {
    return SET_MEMBERSHIP_MODE.NONE_OF;
  }

  if (groupOp === GROUP_OPERATORS.OR && comparisonOp === '==') {
    return SET_MEMBERSHIP_MODE.ANY_OF;
  }

  return null;
};

/**
 * Attempt to collapse a freshly-mapped GROUP into a set-membership node.
 *
 * The group collapses only when EVERY child is a literal comparison on the SAME
 * path with the SAME operator, the operator/group pairing is one of the two
 * lossless idioms, and there are at least MIN_SET_MEMBERSHIP_VALUES children.
 *
 * @param {{ op: string, children: Array }} group
 * @returns {Object|null} a setMembership node, or null when no collapse applies
 */
const tryCollapseToSetMembership = ({ op, children }) => {
  if (children.length < MIN_SET_MEMBERSHIP_VALUES) {
    return null;
  }

  const first = children[0];

  if (first.kind !== NODE_KIND.COMPARISON) {
    return null;
  }

  const mode = resolveSetMembershipMode(op, first.operator);

  if (_.isNil(mode)) {
    return null;
  }

  const allMatch = children.every(
    child =>
      child.kind === NODE_KIND.COMPARISON &&
      child.path === first.path &&
      child.operator === first.operator &&
      !_.isNil(child.value) &&
      child.value !== '',
  );

  if (!allMatch) {
    return null;
  }

  // Dedupe values, preserving first-seen order.
  const values = children
    .map(child => child.value)
    .filter((value, index, all) => all.indexOf(value) === index);

  return {
    kind: NODE_KIND.SET_MEMBERSHIP,
    path: first.path,
    mode,
    values,
    valueType: first.valueType ?? null,
    id: makeId(),
  };
};

/**
 * Whether a dot-path resolves to a boolean field in the schema. Used to decide
 * whether a bare field reference (no comparison operator) is an implicit
 * `field == true` condition the visual builder can render, versus an unknown
 * token that must stay raw (so invalid input still shows as Advanced-only).
 *
 * @param {string} path
 * @param {Object} [schemaParams]
 * @returns {boolean}
 */
const isBooleanFieldPath = (path, schemaParams) => {
  if (_.isNil(schemaParams?.entities)) {
    return false;
  }

  return resolveFieldTypeStr(path, schemaParams) === 'bool';
};

/**
 * Map one AST node to a builder model node.
 * Unsupported shapes produce { kind: 'raw', text: <emitted text> }.
 *
 * @param {Object} astNode
 * @param {Object} [schemaParams] Schema used to recognise bare boolean fields.
 * @returns {Object}
 */
const astNodeToModel = (astNode, schemaParams) => {
  if (_.isNil(astNode)) {
    return makeRaw('');
  }

  const { type, value, operands = [] } = astNode;

  // ---- Group: AND / OR -------------------------------------------------------
  if (
    type === LEX_TOKEN_TYPES.function &&
    (value === GROUP_OPERATORS.AND || value === GROUP_OPERATORS.OR)
  ) {
    const children = operands.map(child => safeAstNodeToModel(child, schemaParams));

    const setMembership = tryCollapseToSetMembership({ op: value, children });

    if (!_.isNil(setMembership)) {
      return setMembership;
    }

    return { kind: NODE_KIND.GROUP, op: value, children, id: makeId() };
  }

  // ---- SWITCH(CASE(condition, result), ...) ----------------------------------
  if (type === LEX_TOKEN_TYPES.function && value === SWITCH_FUNCTION_ENUM.SWITCH) {
    // A trailing default/else operand (any non-CASE operand) cannot be
    // represented by the model (no default row) and would be silently dropped
    // on re-emit. Honor the non-lossless invariant: degrade the whole SWITCH to
    // RAW so the default is preserved verbatim rather than lost.
    const hasNonCaseOperand = operands.some(
      op => !(op?.type === LEX_TOKEN_TYPES.function && op?.value === SWITCH_FUNCTION_ENUM.CASE),
    );

    if (hasNonCaseOperand) {
      return makeRaw(astToText(astNode));
    }

    const cases = operands
      .filter(
        op => op?.type === LEX_TOKEN_TYPES.function && op?.value === SWITCH_FUNCTION_ENUM.CASE,
      )
      .map(caseNode => {
        const [conditionAst, resultAst] = caseNode.operands ?? [];
        const condition = conditionAst
          ? astNodeToModel(conditionAst, schemaParams)
          : makeGroup(GROUP_OPERATORS.AND);
        const result = resultAst ? String(resultAst.value ?? '') : '';
        const resultType = resultAst?.typeOfValue ?? null;

        return makeSwitchCase({ condition, result, resultType });
      });

    const initialCases = cases.length ? cases : [makeBlankSwitchCase()];

    return { kind: NODE_KIND.SWITCH, cases: initialCases, id: makeId() };
  }

  // ---- Collection: ANY / ALL -------------------------------------------------
  if (type === LEX_TOKEN_TYPES.function && COLLECTION_FUNCTIONS[value]) {
    // operands after parser injection: [arrayField, iteratorValue, predicate]
    // The iterator operand has typeOfValue === 'iterator'
    const arrayOperand = operands[0];
    const arrayPath = arrayOperand?.value ?? '';

    const iteratorOperand = operands.find(op => op?.typeOfValue === 'iterator');
    const iteratorName = iteratorOperand?.value ?? deriveIteratorName(arrayPath) ?? '';

    // Predicate is the last non-iterator operand
    const predicateOperand = operands.findLast(op => op?.typeOfValue !== 'iterator');
    const predicate = predicateOperand
      ? safeAstNodeToModel(predicateOperand, schemaParams)
      : makeGroup('AND');

    return {
      kind: NODE_KIND.COLLECTION,
      fn: value,
      arrayPath,
      iteratorName,
      predicate,
      id: makeId(),
    };
  }

  // ---- Comparison: binary operator -------------------------------------------
  if (type === LEX_TOKEN_TYPES.operator && COMPARISON_OPERATORS.has(value)) {
    const lhs = operands[0];
    const rhs = operands[1];

    // ---- relativeDate: ADD_X(field, n) <cmp> DATE_NOW(?) -------------------
    // Detect BOTH operand orders and normalise so the model always stores the
    // date-fn on the "left" (path/dateFn/amount side) and the comparator is
    // expressed relative to that convention.
    const isDateFnNode = node =>
      node?.type === LEX_TOKEN_TYPES.function && DATE_FN_SET.has(node?.value);

    const isDateNowNode = node =>
      node?.type === LEX_TOKEN_TYPES.function && node?.value === 'DATE_NOW';

    const FLIP_COMPARATOR = { '<': '>', '>': '<', '<=': '>=', '>=': '<=' };

    const tryExtractDateFn = (dateFnNode, dateNowNode, comparator) => {
      if (!isDateFnNode(dateFnNode) || !isDateNowNode(dateNowNode)) {
        return null;
      }

      const [fieldArg, amountArg] = dateFnNode.operands ?? [];

      if (fieldArg?.type !== LEX_TOKEN_TYPES.field) {
        return null;
      }

      // Amount must be a plain integer — accept value/decimal/int literal nodes
      const rawAmount = amountArg?.value;

      if (_.isNil(rawAmount)) {
        return null;
      }

      const amount = Number(rawAmount);

      if (!Number.isFinite(amount)) {
        return null;
      }

      // nowArg: DATE_NOW() has zero or one string operand
      const nowArgNode = dateNowNode.operands?.[0];
      const nowArg =
        nowArgNode?.type === LEX_TOKEN_TYPES.value && nowArgNode?.typeOfValue !== 'iterator'
          ? String(nowArgNode.value)
          : null;

      return {
        kind: NODE_KIND.RELATIVE_DATE,
        path: fieldArg.value,
        dateFn: dateFnNode.value,
        amount,
        comparator,
        nowArg,
        id: makeId(),
      };
    };

    // Order 1: ADD_X(field, n) <cmp> DATE_NOW(...)
    const direct = tryExtractDateFn(lhs, rhs, value);

    if (!_.isNil(direct)) {
      return direct;
    }

    // Order 2: DATE_NOW(...) <cmp> ADD_X(field, n)  → flip comparator
    const flipped = FLIP_COMPARATOR[value];

    if (flipped) {
      const reversed = tryExtractDateFn(rhs, lhs, flipped);

      if (!_.isNil(reversed)) {
        return reversed;
      }
    }

    // ---- field <cmp> DATE('YYYY-MM-DD', 'UTC') — date literal constructor ----
    // When the RHS is a DATE(dateArg, timezone) function call, map it to a
    // comparison with valueType 'date' and the bare date string as value.
    // The schema's DATE takes (date, timezone); the timezone is normalized to
    // 'UTC' on re-emit, so only the first argument is read here.
    const lhsIsField = lhs?.type === LEX_TOKEN_TYPES.field && !isSpecField(lhs);
    const rhsIsDateConstructor =
      rhs?.type === LEX_TOKEN_TYPES.function &&
      rhs?.value === 'DATE' &&
      (rhs?.operands ?? []).length >= 1 &&
      (rhs.operands[0]?.type === LEX_TOKEN_TYPES.value ||
        rhs.operands[0]?.type === LEX_TOKEN_TYPES.date);

    if (lhsIsField && rhsIsDateConstructor) {
      const innerArg = rhs.operands[0];
      // Strip any surrounding single quotes from the raw argument value
      const rawDate = String(innerArg.value ?? '').replace(/^'|'$/g, '');

      return {
        kind: NODE_KIND.COMPARISON,
        path: lhs.value,
        operator: value,
        value: rawDate,
        valueType: 'date',
        id: makeId(),
      };
    }

    // ---- Simple field <cmp> literal comparison ------------------------------
    // Only support field == literal (not field == field or arithmetic)
    const rhsIsLiteral =
      rhs?.type === LEX_TOKEN_TYPES.value ||
      rhs?.type === LEX_TOKEN_TYPES.bool ||
      rhs?.type === LEX_TOKEN_TYPES.decimal ||
      rhs?.type === LEX_TOKEN_TYPES.date;

    if (lhsIsField && rhsIsLiteral) {
      return {
        kind: NODE_KIND.COMPARISON,
        path: lhs.value,
        operator: value,
        value: String(rhs.value),
        valueType: rhs.typeOfValue ?? null,
        id: makeId(),
      };
    }

    // ---- Generalized valueModel comparison (lhs/rhs are complex expressions) --
    // Only map when BOTH sides resolve to clean (non-raw) value models.
    const lhsVm = astNodeToValueModel(lhs);
    const rhsVm = astNodeToValueModel(rhs);

    if (valueModelIsClean(lhsVm) && valueModelIsClean(rhsVm)) {
      return {
        kind: NODE_KIND.COMPARISON,
        lhs: lhsVm,
        rhs: rhsVm,
        operator: value,
        id: makeId(),
      };
    }

    // Fall through to raw
  }

  // ---- Scalar function at root level -----------------------------------------
  if (type === LEX_TOKEN_TYPES.function && !RESERVED_FN_NAMES.has(value)) {
    const isArrayFn = ARRAY_FUNCTIONS_WITH_ITERATORS.includes(value);
    const filteredOperands = isArrayFn
      ? operands.filter(op => op?.typeOfValue !== 'iterator')
      : operands;
    const args = filteredOperands.map(astNodeToValueModel);

    if (args.every(valueModelIsClean)) {
      return { kind: NODE_KIND.FUNCTION, fn: value, args, id: makeId() };
    }
  }

  // ---- NOT wrapping a non-negated GROUP → negated group ----------------------
  // NOT(AND(...)) / NOT(OR(...)) become { kind: 'group', negated: true, ... }.
  // Anything else NOT wraps (comparison, collection, double-negation) stays RAW.
  if (type === LEX_TOKEN_TYPES.function && value === 'NOT' && operands.length === 1) {
    const inner = astNodeToModel(operands[0], schemaParams);

    if (inner.kind === NODE_KIND.GROUP && !inner.negated) {
      return { ...inner, negated: true };
    }

    return makeRaw(astToText(astNode));
  }

  // ---- Arithmetic at root level -----------------------------------------------
  if (type === LEX_TOKEN_TYPES.operator && ARITHMETIC_OPS.has(value)) {
    const leftVm = astNodeToValueModel(operands[0]);
    const rightVm = astNodeToValueModel(operands[1]);

    // Apply same "would need unparseable parens" guard as astNodeToValueModel.
    if (valueModelIsClean(leftVm) && valueModelIsClean(rightVm)) {
      const parentPri = ARITH_PRIORITY(value);
      const isNonCommutative = value === '-' || value === '/';

      const rightNeedsParens =
        rightVm.kind === VALUE_KIND.ARITHMETIC &&
        isNonCommutative &&
        ARITH_PRIORITY(rightVm.op) === parentPri;

      const leftNeedsParens =
        leftVm.kind === VALUE_KIND.ARITHMETIC && ARITH_PRIORITY(leftVm.op) < parentPri;

      if (!leftNeedsParens && !rightNeedsParens) {
        return { kind: NODE_KIND.ARITHMETIC, op: value, operands: [leftVm, rightVm], id: makeId() };
      }
    }
  }

  // ---- Bare boolean field used directly as a condition -----------------------
  // A field reference with no comparison operator (e.g. `OR(X, Y)`) is an
  // implicit `field == true`. Model it as a bool comparison flagged `bareBool`
  // so it renders in the visual builder (reusing the existing bool ConditionRow)
  // yet re-emits as the bare field path — lossless: `X`, never `X == true`.
  if (
    type === LEX_TOKEN_TYPES.field &&
    !isSpecField(astNode) &&
    value &&
    isBooleanFieldPath(value, schemaParams)
  ) {
    return {
      kind: NODE_KIND.COMPARISON,
      path: value,
      operator: '==',
      value: 'true',
      valueType: 'bool',
      bareBool: true,
      id: makeId(),
    };
  }

  // ---- Anything else → raw ---------------------------------------------------
  return makeRaw(astToText(astNode));
};

/**
 * Map one child AST node to a model node, isolating failures so a throwing child
 * degrades to a RAW child instead of collapsing the whole group to a single raw.
 *
 * @param {Object} childAst
 * @param {Object} [schemaParams] Schema used to recognise bare boolean fields.
 * @returns {Object}
 */
const safeAstNodeToModel = (childAst, schemaParams) => {
  try {
    return astNodeToModel(childAst, schemaParams);
  } catch {
    return makeRaw(astToText(childAst));
  }
};

// AST parse helper

/**
 * Run the appropriate syntax parser and return the root AST data node.
 *
 * @param {{ tokens: Array, schemaParams: Object, isOnlyBooleanAllowed: boolean, isNewExpressionsParsingLogicEnabled: boolean }} params
 * @returns {Object|null|undefined}
 */
const parseAst = ({
  tokens,
  schemaParams,
  isOnlyBooleanAllowed,
  isNewExpressionsParsingLogicEnabled,
}) => {
  if (isNewExpressionsParsingLogicEnabled) {
    const parser = new SyntaxParser(schemaParams);
    const { data } = parser.parse({ tokens, isOnlyBooleanAllowed });

    return data;
  }

  const { data } = syntaxParser({ tokens, schemaParams, isOnlyBooleanAllowed });

  return data;
};

// Text → model (public)

/**
 * Parse expression text into a builder model.
 *
 * @param {{
 *   text: string,
 *   schemaParams: Object,
 *   isOnlyBooleanAllowed?: boolean,
 *   isNewExpressionsParsingLogicEnabled?: boolean
 * }} params
 * @returns {{ model: Object, isFullyVisual: boolean }|null}
 */
export const parseTextToModel = ({
  text,
  schemaParams,
  isOnlyBooleanAllowed = false,
  isNewExpressionsParsingLogicEnabled = false,
}) => {
  if (!text || !text.trim()) {
    return { model: makeGroup(GROUP_OPERATORS.AND), isFullyVisual: true };
  }

  try {
    const lexicalParser = new LexicalParser(schemaParams);
    const tokens = lexicalParser.parse(text);

    if (!tokens || !tokens.length) {
      return null;
    }

    const data = parseAst({
      tokens,
      schemaParams,
      isOnlyBooleanAllowed,
      isNewExpressionsParsingLogicEnabled,
    });

    if (_.isNil(data)) {
      return null;
    }

    const model = astNodeToModel(data, schemaParams);
    const isFullyVisual = !containsRaw(model);

    return { model, isFullyVisual };
  } catch {
    return null;
  }
};

// Model → text (public)

/**
 * Determine whether a model comparison value should be quoted in emitted text.
 *
 * @param {string|null|undefined} valueType
 * @returns {boolean}
 */
const valueNeedsQuotes = valueType => {
  if (_.isNil(valueType)) {
    return false;
  }

  if (UNQUOTED_TYPES.has(valueType)) {
    return false;
  }

  if (STRING_TYPES.includes(valueType)) {
    return true;
  }

  // Anything not in the known-unquoted set (enums, unknown named types) → quote
  return true;
};

/**
 * Emit a single `path operator value` comparison clause, applying the same
 * quoting rules used by both comparison nodes and set-membership clauses.
 * Date literals are wrapped in DATE('…', 'UTC') so the engine receives a date
 * type rather than a string.
 *
 * @param {{ path: string, operator: string, value: string, valueType: string|null }} clause
 * @returns {string}
 */
const emitComparisonText = ({ path, operator, value, valueType }) => {
  if (valueType === 'date' || valueType === LEX_TOKEN_TYPES.date) {
    return `${path} ${operator} DATE('${value}', 'UTC')`;
  }

  const emittedValue = valueNeedsQuotes(valueType) ? `'${value}'` : value;

  return `${path} ${operator} ${emittedValue}`;
};

/**
 * Emit the result-value side of a CASE clause, applying date wrapping and quoting.
 *
 * @param {string|null} resultType
 * @param {string} result
 * @returns {string}
 */
const emitSwitchResultValue = (resultType, result) => {
  if (resultType === 'date') {
    return `DATE('${result}', 'UTC')`;
  }

  return valueNeedsQuotes(resultType) ? `'${result}'` : result;
};

/**
 * Emit a builder model node back to expression text.
 *
 * @param {Object} node
 * @returns {string}
 */
export const emitModelToText = node => {
  if (_.isNil(node)) {
    return '';
  }

  const { kind } = node;

  if (kind === NODE_KIND.RAW) {
    return node.text;
  }

  if (kind === NODE_KIND.COMPARISON) {
    // Generalised shape: { lhs: valueModel, rhs: valueModel, operator }
    if (!_.isNil(node.lhs) && !_.isNil(node.rhs)) {
      const lhsText = emitValueModel(node.lhs);
      const rhsText = emitValueModel(node.rhs);

      if (!lhsText || !rhsText) {
        return '';
      }

      return `${lhsText} ${node.operator} ${rhsText}`;
    }

    // Bare boolean field — re-emit as the field path alone (the `bareBool`
    // idiom: `X`, not `X == true`). Guarded on the exact implicit-true shape, so
    // any edit (operator/value/type change) falls through to a real comparison
    // and an author-typed `field == true` (no flag) is never collapsed.
    if (
      node.bareBool &&
      node.operator === '==' &&
      node.value === 'true' &&
      node.valueType === 'bool' &&
      node.path
    ) {
      return node.path;
    }

    // Legacy shape: { path, operator, value, valueType }
    const { path, operator, value, valueType } = node;

    // Skip incomplete conditions silently
    if (!path || (!value && value !== '0' && value !== 'false' && value !== 'true')) {
      return '';
    }

    // Date literals must be wrapped in DATE('…', 'UTC') so the engine receives a date
    // type rather than a string — `'YYYY-MM-DD'` alone fails type checking.
    return emitComparisonText({ path, operator, value, valueType });
  }

  if (kind === NODE_KIND.FUNCTION) {
    const argTexts = (node.args ?? []).map(emitValueModel);

    return `${node.fn}(${argTexts.join(', ')})`;
  }

  if (kind === NODE_KIND.ARITHMETIC) {
    return emitValueModel(node);
  }

  if (kind === NODE_KIND.RELATIVE_DATE) {
    const { path, dateFn, amount, comparator, nowArg } = node;

    if (!path || !dateFn) {
      return '';
    }

    const nowArgText = nowArg ? `'${nowArg}'` : '';
    const dateNowText = `DATE_NOW(${nowArgText})`;

    return `${dateFn}(${path}, ${Math.round(amount)}) ${comparator} ${dateNowText}`;
  }

  if (kind === NODE_KIND.SET_MEMBERSHIP) {
    const { path, mode, values, valueType } = node;

    if (!path) {
      return '';
    }

    const cleanValues = (values ?? []).filter(value => !_.isNil(value) && value !== '');

    if (!cleanValues.length) {
      return '';
    }

    const groupOp = mode === SET_MEMBERSHIP_MODE.NONE_OF ? GROUP_OPERATORS.AND : GROUP_OPERATORS.OR;
    const operator = mode === SET_MEMBERSHIP_MODE.NONE_OF ? '!=' : '==';

    const clauses = cleanValues.map(value =>
      emitComparisonText({ path, operator, value, valueType }),
    );

    if (clauses.length === 1) {
      return clauses[0];
    }

    return `${groupOp}(${clauses.join(', ')})`;
  }

  if (kind === NODE_KIND.COLLECTION) {
    const { fn, arrayPath, predicate } = node;
    const predicateText = emitModelToText(predicate);

    return `${fn}(${arrayPath}, ${predicateText})`;
  }

  if (kind === NODE_KIND.SWITCH) {
    const { cases } = node;
    const caseTexts = (cases ?? [])
      .map(c => {
        const conditionText = emitModelToText(c.condition);
        const { result, resultType } = c;

        if (
          !conditionText ||
          (!result && result !== '0' && result !== 'false' && result !== 'true')
        ) {
          return null;
        }

        const emittedResult = emitSwitchResultValue(resultType, result);

        return `CASE(${conditionText}, ${emittedResult})`;
      })
      .filter(t => t !== null);

    if (!caseTexts.length) {
      return '';
    }

    return `SWITCH(${caseTexts.join(', ')})`;
  }

  if (kind === NODE_KIND.GROUP) {
    const { op, children, negated } = node;
    const childTexts = children
      .map(child => {
        const text = emitModelToText(child);

        if (text !== '') {
          return text;
        }

        // Preserve structural marker for nested groups that have children but
        // all of their children emit empty (e.g. a newly-seeded blank group).
        if (child.kind === NODE_KIND.GROUP && child.children.length > 0) {
          return `${child.op}()`;
        }

        return '';
      })
      .filter(t => t !== '');

    if (!childTexts.length) {
      return '';
    }

    const inner = `${op}(${childTexts.join(', ')})`;

    return negated ? `NOT(${inner})` : inner;
  }

  return '';
};

// Helpers: model inspection

/**
 * Return true if the model tree contains at least one raw node.
 *
 * @param {Object} node
 * @returns {boolean}
 */
const containsRaw = node => {
  if (_.isNil(node)) {
    return false;
  }

  if (node.kind === NODE_KIND.RAW) {
    return true;
  }

  if (node.kind === NODE_KIND.GROUP) {
    return node.children.some(containsRaw);
  }

  if (node.kind === NODE_KIND.COLLECTION) {
    return containsRaw(node.predicate);
  }

  if (node.kind === NODE_KIND.SWITCH) {
    return (node.cases ?? []).some(c => containsRaw(c.condition));
  }

  if (node.kind === NODE_KIND.COMPARISON && !_.isNil(node.lhs)) {
    return !valueModelIsClean(node.lhs) || !valueModelIsClean(node.rhs);
  }

  if (node.kind === NODE_KIND.FUNCTION) {
    return !(node.args ?? []).every(valueModelIsClean);
  }

  if (node.kind === NODE_KIND.ARITHMETIC) {
    return !(node.operands ?? []).every(valueModelIsClean);
  }

  return false;
};

// Tree edit helpers (pure — return new model trees)

/**
 * Find a node by id, returning it or null.
 *
 * @param {Object} model
 * @param {string} nodeId
 * @returns {Object|null}
 */
export const findNode = (model, nodeId) => {
  if (_.isNil(model)) {
    return null;
  }

  if (model.id === nodeId) {
    return model;
  }

  if (model.kind === NODE_KIND.GROUP) {
    return model.children.reduce((found, child) => found ?? findNode(child, nodeId), null);
  }

  if (model.kind === NODE_KIND.COLLECTION) {
    return findNode(model.predicate, nodeId);
  }

  if (model.kind === NODE_KIND.SWITCH) {
    return (model.cases ?? []).reduce((found, c) => found ?? findNode(c.condition, nodeId), null);
  }

  return null;
};

/**
 * Walk the tree and apply `transform` to the node with the given id.
 * Returns a new tree with the node replaced by transform(node).
 *
 * @param {Object} node
 * @param {string} targetId
 * @param {function(Object): Object} transform
 * @returns {Object}
 */
const walkAndReplace = (node, targetId, transform) => {
  if (_.isNil(node)) {
    return node;
  }

  if (node.id === targetId) {
    return transform(node);
  }

  if (node.kind === NODE_KIND.GROUP) {
    return {
      ...node,
      children: node.children.map(child => walkAndReplace(child, targetId, transform)),
    };
  }

  if (node.kind === NODE_KIND.COLLECTION) {
    return { ...node, predicate: walkAndReplace(node.predicate, targetId, transform) };
  }

  if (node.kind === NODE_KIND.SWITCH) {
    return {
      ...node,
      cases: (node.cases ?? []).map(c => ({
        ...c,
        condition: walkAndReplace(c.condition, targetId, transform),
      })),
    };
  }

  return node;
};

/**
 * Add a blank comparison condition to the group identified by groupId.
 *
 * @param {Object} model
 * @param {string} groupId
 * @returns {Object}
 */
export const addCondition = (model, groupId) =>
  walkAndReplace(model, groupId, group => ({
    ...group,
    children: [...group.children, makeBlankComparison()],
  }));

/**
 * Add a blank comparison condition and return both the new model and the new node's id.
 *
 * @param {Object} model
 * @param {string} groupId
 * @returns {{ model: Object, newNodeId: string }}
 */
export const addConditionWithId = (model, groupId) => {
  const blank = makeBlankComparison();
  const newModel = walkAndReplace(model, groupId, group => ({
    ...group,
    children: [...group.children, blank],
  }));

  return { model: newModel, newNodeId: blank.id };
};

/**
 * Add a nested group inside the group identified by groupId.
 *
 * @param {Object} model
 * @param {string} groupId
 * @param {'AND'|'OR'} op
 * @param {{ seedCondition?: boolean }} [opts]
 * @returns {Object}
 */
export const addGroup = (model, groupId, op, opts) => {
  const children = opts?.seedCondition ? [makeBlankComparison()] : [];

  return walkAndReplace(model, groupId, group => ({
    ...group,
    children: [...group.children, makeGroup(op, children)],
  }));
};

/**
 * Add a nested group and return both the new model and the seed condition node's id
 * (when seedCondition is true) or the group's id (when seedCondition is false).
 *
 * @param {Object} model
 * @param {string} groupId
 * @param {'AND'|'OR'} op
 * @param {{ seedCondition?: boolean }} [opts]
 * @returns {{ model: Object, newNodeId: string }}
 */
export const addGroupWithId = (model, groupId, op, opts) => {
  const seed = opts?.seedCondition ? makeBlankComparison() : null;
  const children = seed ? [seed] : [];
  const newGroup = makeGroup(op, children);
  const newModel = walkAndReplace(model, groupId, group => ({
    ...group,
    children: [...group.children, newGroup],
  }));

  return { model: newModel, newNodeId: seed ? seed.id : newGroup.id };
};

/**
 * Add a collection (ANY/ALL) node to the group identified by groupId.
 *
 * @param {Object} model
 * @param {string} groupId
 * @param {{ arrayPath: string, iteratorName?: string }} collectionParams
 * @returns {Object}
 */
export const addCollection = (model, groupId, { arrayPath, iteratorName }) =>
  walkAndReplace(model, groupId, group => ({
    ...group,
    children: [
      ...group.children,
      {
        kind: NODE_KIND.COLLECTION,
        fn: COLLECTION_FUNCTIONS.ANY,
        arrayPath,
        iteratorName: iteratorName ?? deriveIteratorName(arrayPath) ?? '',
        predicate: makeGroup(GROUP_OPERATORS.AND),
        id: makeId(),
      },
    ],
  }));

/**
 * Update a node by merging `patch` into it.
 *
 * @param {Object} model
 * @param {string} nodeId
 * @param {Object} patch
 * @returns {Object}
 */
export const updateNode = (model, nodeId, patch) =>
  walkAndReplace(model, nodeId, node => ({ ...node, ...patch }));

/**
 * Remove the node with nodeId from its parent.
 * Removing the last child of the root group keeps the empty root group.
 * Removing the root itself is a no-op.
 *
 * @param {Object} model
 * @param {string} nodeId
 * @returns {Object}
 */
export const removeNode = (model, nodeId) => {
  // Can't remove the root
  if (model.id === nodeId) {
    return model;
  }

  return removeFromNode(model, nodeId);
};

/**
 * Recursively remove a node by id from within a subtree.
 *
 * @param {Object} node
 * @param {string} nodeId
 * @returns {Object}
 */
const removeFromNode = (node, nodeId) => {
  if (_.isNil(node)) {
    return node;
  }

  if (node.kind === NODE_KIND.GROUP) {
    const childFiltered = node.children.filter(child => child.id !== nodeId);

    if (childFiltered.length !== node.children.length) {
      return { ...node, children: childFiltered };
    }

    return { ...node, children: node.children.map(child => removeFromNode(child, nodeId)) };
  }

  if (node.kind === NODE_KIND.COLLECTION) {
    if (node.predicate?.id === nodeId) {
      return { ...node, predicate: makeGroup(GROUP_OPERATORS.AND) };
    }

    return { ...node, predicate: removeFromNode(node.predicate, nodeId) };
  }

  if (node.kind === NODE_KIND.SWITCH) {
    return {
      ...node,
      cases: (node.cases ?? []).map(c => ({
        ...c,
        condition: removeFromNode(c.condition, nodeId),
      })),
    };
  }

  return node;
};

/**
 * Append a blank CASE row to the SWITCH node.
 *
 * @param {Object} switchNode
 * @returns {Object}
 */
export const addCase = switchNode => ({
  ...switchNode,
  cases: [...(switchNode.cases ?? []), makeBlankSwitchCase()],
});

/**
 * Remove the CASE at the given index from the SWITCH node.
 * Preserves at least one row.
 *
 * @param {Object} switchNode
 * @param {number} index
 * @returns {Object}
 */
export const removeCase = (switchNode, index) => {
  const cases = switchNode.cases ?? [];

  if (cases.length <= 1) {
    return switchNode;
  }

  return { ...switchNode, cases: cases.filter((_, i) => i !== index) };
};

/**
 * Apply a patch to the CASE at the given index.
 * Patch may update `result`, `resultType`, or `condition`.
 *
 * @param {Object} switchNode
 * @param {number} index
 * @param {Object} patch
 * @returns {Object}
 */
export const updateCase = (switchNode, index, patch) => ({
  ...switchNode,
  cases: (switchNode.cases ?? []).map((c, i) => (i === index ? { ...c, ...patch } : c)),
});

// Value-slot edit helpers (public)

/**
 * Replace the value at `argIndex` in a FUNCTION node's args array.
 * Returns a new FUNCTION model node.
 *
 * @param {Object} fnNode - A NODE_KIND.FUNCTION model node
 * @param {number} argIndex
 * @param {Object} newValueModel
 * @returns {Object}
 */
export const updateFunctionArg = (fnNode, argIndex, newValueModel) => ({
  ...fnNode,
  args: (fnNode.args ?? []).map((a, i) => (i === argIndex ? newValueModel : a)),
});

/**
 * Append a blank literal value slot to a variadic FUNCTION node.
 * Returns a new FUNCTION model node.
 *
 * @param {Object} fnNode - A NODE_KIND.FUNCTION model node
 * @returns {Object}
 */
export const addFunctionArg = fnNode => ({
  ...fnNode,
  args: [...(fnNode.args ?? []), makeLiteral('', null)],
});

/**
 * Remove the arg at `argIndex` from a variadic FUNCTION node.
 * Preserves at least one arg slot.
 *
 * @param {Object} fnNode - A NODE_KIND.FUNCTION model node
 * @param {number} argIndex
 * @returns {Object}
 */
export const removeFunctionArg = (fnNode, argIndex) => {
  const args = fnNode.args ?? [];

  if (args.length <= 1) {
    return fnNode;
  }

  return { ...fnNode, args: args.filter((_, i) => i !== argIndex) };
};

/**
 * Switch a value slot to a different kind.
 * Returns a blank value model of the target kind.
 *
 * @param {'fieldRef'|'literal'|'function'|'arithmetic'|'raw'} targetKind
 * @param {string} [fnName] - Required when targetKind === 'function'
 * @param {string} [op] - Required when targetKind === 'arithmetic'
 * @returns {Object}
 */
export const setValueKind = (targetKind, fnName, op) => {
  if (targetKind === VALUE_KIND.FIELD_REF) {
    return makeFieldRef('');
  }

  if (targetKind === VALUE_KIND.LITERAL) {
    return makeLiteral('', null);
  }

  if (targetKind === VALUE_KIND.FUNCTION) {
    return makeFunctionValue(fnName ?? '', [makeLiteral('', null)]);
  }

  if (targetKind === VALUE_KIND.ARITHMETIC) {
    return makeArithmeticValue(op ?? '+', makeFieldRef(''), makeFieldRef(''));
  }

  return makeRawValue('');
};

/**
 * Update the scalar properties of a value model (path, value, valueType, fn, op).
 * Returns a new value model node.
 *
 * @param {Object} vm
 * @param {Object} patch
 * @returns {Object}
 */
export const updateValue = (vm, patch) => ({ ...vm, ...patch });

/**
 * Append a blank fieldRef operand to an ARITHMETIC value model.
 * Returns a new ARITHMETIC value model node.
 *
 * @param {Object} arithmeticVm - A VALUE_KIND.ARITHMETIC value model
 * @returns {Object}
 */
export const addArithmeticOperand = arithmeticVm => ({
  ...arithmeticVm,
  operands: [...(arithmeticVm.operands ?? []), makeFieldRef('')],
});

/**
 * Remove the operand at `index` from an ARITHMETIC value model.
 * Preserves at least two operand slots.
 *
 * @param {Object} arithmeticVm - A VALUE_KIND.ARITHMETIC value model
 * @param {number} index
 * @returns {Object}
 */
export const removeArithmeticOperand = (arithmeticVm, index) => {
  const operands = arithmeticVm.operands ?? [];

  if (operands.length <= 2) {
    return arithmeticVm;
  }

  return { ...arithmeticVm, operands: operands.filter((_, i) => i !== index) };
};

// Node-kind conversion helpers (public)

/**
 * Replace the COMPARISON node identified by `nodeId` in `rootModel` with a
 * SET_MEMBERSHIP node of the given `mode`.
 *
 * Carries over path and valueType.  Seeds `values` with the current literal
 * value when one is set, otherwise starts with an empty list.
 *
 * @param {Object} rootModel
 * @param {string} nodeId
 * @param {'anyOf'|'noneOf'} mode
 * @returns {Object} new root model
 */
export const comparisonToSetMembership = (rootModel, nodeId, mode) =>
  walkAndReplace(rootModel, nodeId, node => ({
    kind: NODE_KIND.SET_MEMBERSHIP,
    id: node.id,
    path: node.path ?? '',
    mode,
    values: node.value && node.value !== '' ? [node.value] : [],
    valueType: node.valueType ?? null,
  }));

/**
 * Replace the SET_MEMBERSHIP node identified by `nodeId` in `rootModel` with a
 * COMPARISON node using the given `operator`.
 *
 * Carries over path and valueType.  Seeds `value` with the first element of
 * `node.values` (if any) so a single-value set-membership survives the round-trip.
 *
 * @param {Object} rootModel
 * @param {string} nodeId
 * @param {string} operator
 * @returns {Object} new root model
 */
export const setMembershipToComparison = (rootModel, nodeId, operator) =>
  walkAndReplace(rootModel, nodeId, node => ({
    kind: NODE_KIND.COMPARISON,
    id: node.id,
    path: node.path ?? '',
    operator: operator ?? '==',
    value: (node.values ?? [])[0] ?? '',
    valueType: node.valueType ?? null,
  }));

/**
 * Reorder a child within its parent group from `fromIndex` to `toIndex`.
 * Immutable — returns a new root model. All node ids and data are preserved.
 *
 * @param {Object} rootModel
 * @param {string} parentGroupId
 * @param {number} fromIndex
 * @param {number} toIndex
 * @returns {Object}
 */
export const reorderChildren = (rootModel, parentGroupId, fromIndex, toIndex) =>
  walkAndReplace(rootModel, parentGroupId, group => {
    const { children } = group;

    if (fromIndex < 0 || fromIndex >= children.length) {
      return group;
    }

    const next = [...children];
    const [moved] = next.splice(fromIndex, 1);

    if (_.isNil(moved)) {
      return group;
    }

    const clampedToIndex = _.clamp(toIndex, 0, next.length);

    next.splice(clampedToIndex, 0, moved);

    return { ...group, children: next };
  });
